import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FileText,
  Film,
  Loader2,
  Mic2,
  Play,
  ScrollText,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NarrationVoicePickerDialog } from '@/components/NarrationVoicePickerDialog';
import { TtsProviderStyleSelect } from '@/components/tts/TtsProviderStyleSelect';
import { ScriptBasedPipelineDialog } from '@/components/ScriptBasedPipelineDialog';
import api from '@/lib/api';
import { fetchMurfVoiceCatalog } from '@/lib/murfApi';
import { splitScriptIntoContextualScenes } from '@/lib/scriptBasedSceneSplit';
import {
  DEFAULT_NARRATION_STORYBOARD_STYLE,
  NARRATION_STORYBOARD_STYLES,
  storyboardStyleMeta,
} from '@/lib/narrationStoryboardStyles';
import { getIntakeNavPrefill } from '@/lib/intakeNavPrefill';
import { pickOption, readAgentHandoff } from '@/lib/agentHandoff';
import { AgentHandoffGate } from '@/components/createPlan/AgentHandoffGate';
import {
  defaultVoiceStyleForProvider,
  normalizeVoiceStyleForProvider,
  shouldShowProviderStyleSelect,
} from '@/lib/ttsProviderStyles';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const IMAGE_PROVIDER_OPTIONS = [
  { value: 'nano_banana', label: 'Gemini Nano Banana' },
  { value: 'minimax', label: 'MiniMax' },
  { value: 'runway', label: 'Runway' },
  { value: 'veo3', label: 'Veo 3' },
  { value: 'inhouse', label: 'Inhouse' },
];

const providerLabel = {
  elevenlabs: 'ElevenLabs',
  murf: 'Murf',
  minimax: 'MiniMax',
};

export const ScriptBasedFlowPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [title, setTitle] = useState('');
  const [fullScript, setFullScript] = useState('');
  const [language, setLanguage] = useState('hi');
  const [visualStyle, setVisualStyle] = useState(DEFAULT_NARRATION_STORYBOARD_STYLE);
  const [imageProvider, setImageProvider] = useState('nano_banana');
  const [voiceStyle, setVoiceStyle] = useState('storyteller');
  const [ttsProvider, setTtsProvider] = useState('auto');
  const [narratorElevenlabsVoiceId, setNarratorElevenlabsVoiceId] = useState('');
  const [narratorMurfVoiceId, setNarratorMurfVoiceId] = useState('');
  const [narratorMurfLocale, setNarratorMurfLocale] = useState('');
  const [narratorMinimaxVoiceId, setNarratorMinimaxVoiceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [voicePickerOpen, setVoicePickerOpen] = useState(false);
  const [voiceProvider, setVoiceProvider] = useState('elevenlabs');
  const [murfVoices, setMurfVoices] = useState([]);

  const agentHandoff = useMemo(() => readAgentHandoff(location.state), [location.state]);
  const [handoffReady, setHandoffReady] = useState(false);

  useEffect(() => {
    const nav = getIntakeNavPrefill(location.state);
    if (!nav) return;
    const fromAgent = Boolean(readAgentHandoff(location.state));
    if (nav.title) setTitle(nav.title);
    // A brief is not a verbatim script, so agent handoffs only use a real script answer.
    const script = fromAgent ? String(nav.prefill.fullScript || '').trim() : nav.fullScript;
    if (script) setFullScript(script);
    if (nav.language) setLanguage(nav.language);
    if (nav.visualStyle) setVisualStyle(nav.visualStyle);
    if (fromAgent) {
      setImageProvider((prev) => pickOption(nav.prefill.imageProvider, IMAGE_PROVIDER_OPTIONS, prev));
    }
    if (fromAgent) setHandoffReady(true);
  }, [location.state]);

  useEffect(() => {
    if (ttsProvider !== 'murf') return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await fetchMurfVoiceCatalog(api);
        if (!cancelled) setMurfVoices(data?.voices || []);
      } catch {
        if (!cancelled) setMurfVoices([]);
      }
    })();
    return () => { cancelled = true; };
  }, [ttsProvider]);

  const statementPreview = useMemo(() => splitScriptIntoContextualScenes(fullScript), [fullScript]);
  const selectedStyleMeta = useMemo(() => storyboardStyleMeta(visualStyle), [visualStyle]);

  const openVoicePicker = (provider = ttsProvider) => {
    if (!provider || provider === 'auto') {
      toast.info('Choose ElevenLabs, Murf, or MiniMax first');
      return;
    }
    setVoiceProvider(provider);
    setVoicePickerOpen(true);
  };

  const selectVoice = (voice) => {
    if (voiceProvider === 'elevenlabs') setNarratorElevenlabsVoiceId(voice.voice_id);
    if (voiceProvider === 'murf') {
      setNarratorMurfVoiceId(voice.voice_id);
      setNarratorMurfLocale(voice.locale || voice.effective_locale || narratorMurfLocale);
      setVoiceStyle((prev) => normalizeVoiceStyleForProvider('murf', prev, {
        murfVoices,
        murfVoiceId: voice.voice_id,
        murfLocale: voice.locale || voice.effective_locale || narratorMurfLocale,
        language,
      }));
    }
    if (voiceProvider === 'minimax') setNarratorMinimaxVoiceId(voice.voice_id);
    setVoicePickerOpen(false);
    toast.success(`${providerLabel[voiceProvider]} voice selected`);
  };

  const startPipeline = async () => {
    if (!title.trim()) {
      toast.error('Add a title');
      return;
    }
    if (fullScript.trim().length < 20) {
      toast.error('Paste your full script (at least 20 characters)');
      return;
    }
    if (!statementPreview.length) {
      toast.error('Add script text to split into scenes');
      return;
    }
    setLoading(true);
    setJob(null);
    setProgressOpen(true);
    try {
      const payload = {
        title: title.trim(),
        full_script: fullScript.trim(),
        language,
        tone: 'storytelling',
        visual_style: visualStyle,
        image_provider: imageProvider,
        image_aspect_ratio: '16:9',
        voice_style: voiceStyle,
        tts_provider: ttsProvider,
        narrator_elevenlabs_voice_id: narratorElevenlabsVoiceId || null,
        narrator_murf_voice_id: narratorMurfVoiceId || null,
        narrator_murf_locale: narratorMurfLocale || null,
        narrator_minimax_voice_id: narratorMinimaxVoiceId || null,
      };
      const { data } = await api.post('/narration/script-based/projects', payload);
      const nextProjectId = data.project.id;
      const nextJobId = data.job_id;
      setProjectId(nextProjectId);
      setJobId(nextJobId);
      setJob({ id: nextJobId, status: 'pending', progress: 0, message: 'Queued', pipeline_trace: [] });
      toast.success('Pipeline started');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start pipeline');
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const isRunning = job && !['completed', 'failed', 'cancelled'].includes(job.status);
  const isComplete = job?.status === 'completed';

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="script-based-flow-page">
      <AgentHandoffGate
        handoff={agentHandoff}
        ready={handoffReady}
        flowLabel="Script-based flow"
        busy={loading}
        onStart={startPipeline}
        fields={[
          {
            id: 'title',
            label: 'Video title',
            valid: Boolean(title.trim()),
            render: () => (
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. The river that remembered" autoFocus />
            ),
          },
          {
            id: 'script',
            label: 'Your full script',
            hint: 'Used verbatim — at least 20 characters.',
            valid: fullScript.trim().length >= 20,
            render: () => (
              <Textarea value={fullScript} onChange={(e) => setFullScript(e.target.value)} rows={8} placeholder="Paste the finished script…" />
            ),
          },
        ]}
      />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 bg-white/80 border border-slate-100 shadow-sm">
          <ScrollText className="w-4 h-4 text-violet-600" />
          <span className="text-sm font-semibold text-slate-600">Script-based flow</span>
        </div>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">
          Your script, <span className="text-violet-600">verbatim</span>
        </h1>
        <p className="text-slate-600 text-sm sm:text-base max-w-2xl mb-8">
          Paste a finished script. We narrate it and align every word with Whisper.
          Web-scraper stills follow each sentence and stay on screen for 1.5–3 seconds. Other styles use one image per scene.
        </p>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Story title" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Visual style</Label>
                <Select value={visualStyle} onValueChange={setVisualStyle}>
                  <SelectTrigger><SelectValue placeholder="Choose style" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {NARRATION_STORYBOARD_STYLES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {visualStyle === 'web_scraper' ? (
                  <p className="text-xs text-muted-foreground mt-1">
                    Whisper times each sentence. We then scrape or generate stills so every image stays on screen for 1.5–3 seconds.
                  </p>
                ) : null}
              </div>
              <div className="space-y-2">
                <Label>Image provider</Label>
                <Select
                  value={imageProvider}
                  onValueChange={setImageProvider}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {IMAGE_PROVIDER_OPTIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {selectedStyleMeta?.directive ? (
              <p className="text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-100 p-2">
                {selectedStyleMeta.directive}
              </p>
            ) : null}
            <div className="space-y-2">
              <Label>Full script</Label>
              <Textarea
                value={fullScript}
                onChange={(e) => setFullScript(e.target.value)}
                rows={14}
                placeholder="Paste your script. Paragraph breaks (blank lines) prefer new scenes; long paragraphs split by sentence."
                className="font-mono text-sm leading-relaxed"
              />
              <p className="text-xs text-slate-500">
                {statementPreview.length} contextual scene{statementPreview.length === 1 ? '' : 's'} detected
                {statementPreview.length > 0 ? ` · ~${statementPreview.length * 4 + 2} pipeline steps` : ''}
              </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hi">Hindi</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="mr">Marathi</SelectItem>
                    <SelectItem value="ta">Tamil</SelectItem>
                    <SelectItem value="te">Telugu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>TTS provider</Label>
              <div className="flex flex-wrap items-end gap-2">
                <Select
                  value={ttsProvider}
                  onValueChange={(v) => {
                    setTtsProvider(v);
                    if (shouldShowProviderStyleSelect(v)) {
                      setVoiceStyle(defaultVoiceStyleForProvider(v));
                    }
                  }}
                >
                  <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
                    <SelectItem value="murf">Murf</SelectItem>
                    <SelectItem value="minimax">MiniMax</SelectItem>
                  </SelectContent>
                </Select>
                <TtsProviderStyleSelect
                  provider={ttsProvider}
                  value={voiceStyle}
                  onChange={setVoiceStyle}
                  murfVoices={murfVoices}
                  murfVoiceId={narratorMurfVoiceId}
                  murfLocale={narratorMurfLocale}
                  language={language}
                  triggerClassName="w-[min(100%,11rem)] h-9 rounded-md border border-input bg-background px-3 text-sm"
                />
                <Button type="button" variant="secondary" size="sm" onClick={() => openVoicePicker()}>
                  <Mic2 className="w-4 h-4 mr-1" />
                  Pick voice
                </Button>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                size="lg"
                className="w-full sm:flex-1 rounded-full"
                disabled={loading || isRunning}
                onClick={startPipeline}
              >
                {loading || isRunning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Running pipeline…
                  </>
                ) : (
                  <>
                    <TwinneeLogo className="w-4 h-4 mr-2" />
                    Start script-based pipeline
                  </>
                )}
              </Button>
              {projectId && jobId && (
                <Button
                  type="button"
                  size="lg"
                  variant="outline"
                  className="w-full sm:w-auto rounded-full"
                  onClick={() => setProgressOpen(true)}
                >
                  View pipeline
                </Button>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <div className="flex items-center gap-2 mb-4">
              <FileText className="w-5 h-5 text-violet-600" />
              <h2 className="font-semibold text-slate-800">Scene preview</h2>
            </div>
            {statementPreview.length === 0 ? (
              <p className="text-sm text-slate-500">Scene beats appear here as you paste — each maps to one image in the next step.</p>
            ) : (
              <ol className="space-y-3 max-h-[520px] overflow-y-auto pr-1 text-sm">
                {statementPreview.map((stmt, i) => (
                  <li key={`stmt-${i}`} className="rounded-lg border border-slate-200 bg-white p-3">
                    <span className="text-xs font-bold text-violet-600 mr-2">{i + 1}.</span>
                    <span className="text-slate-700 whitespace-pre-wrap">{stmt}</span>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </div>

        {projectId && isComplete && (
          <div className="mt-8 flex flex-wrap gap-3">
            <Button variant="outline" className="rounded-full" onClick={() => navigate(`/narration/${projectId}/scenes`)}>
              <Film className="w-4 h-4 mr-2" />
              Open scenes
            </Button>
            <Button className="rounded-full" onClick={() => navigate(`/narration/${projectId}/video-editor`)}>
              <Play className="w-4 h-4 mr-2" />
              Video editor
            </Button>
          </div>
        )}
      </motion.div>

      <ScriptBasedPipelineDialog
        open={progressOpen}
        onOpenChange={setProgressOpen}
        jobId={jobId}
        projectId={projectId}
        onJobChange={setJob}
        onJobIdChange={setJobId}
      />

      <NarrationVoicePickerDialog
        open={voicePickerOpen}
        onOpenChange={setVoicePickerOpen}
        provider={voiceProvider}
        language={language}
        onSelectVoice={selectVoice}
      />
    </div>
  );
};
